import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import PhotoGallery from './PhotoGallery';
import UploadForm from './UploadForm';
import Floorplan from './Floorplan';

const Dashboard = ({ logo }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('seating');
  const [selectionMode, setSelectionMode] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Sync activeTab with current route
  useEffect(() => {
    if (location.pathname.includes('/seating')) {
      setActiveTab('seating');
    } else if (location.pathname.includes('/upload')) {
      setActiveTab('upload');
    } else if (location.pathname.includes('/view')) {
      setActiveTab('view');
    } else {
      // Default to seating for /dashboard or any unmatched path
      setActiveTab('seating');
    }
  }, [location.pathname]);

  // Handle upload completion
  const handleUploadComplete = (uploadedFiles) => {
    console.log('Upload completed, refreshing photo gallery:', uploadedFiles);
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-wedding-white to-wedding-light-white flex flex-col">
      <header className="pt-8 pb-6 flex flex-col items-center bg-wedding-white shadow-sm">
        <img
          src={logo}
          alt="Wedding Logo"
          className="w-36 h-36 object-contain mb-3 opacity-95 select-none pointer-events-none"
          draggable="false"
        />
        <h1 className="text-lg font-medium text-wedding-dark-brown/80 tracking-wide">
          Wedding of Joel & Melissa
        </h1>
      </header>

      <nav className="bg-white border-b border-wedding-sage/20 shadow-sm sticky top-0 z-30" role="navigation" aria-label="Main navigation">
        <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex justify-center items-center py-2 sm:py-3">
            <div className="flex items-center justify-between w-full max-w-sm sm:max-w-md lg:max-w-lg" role="tablist" aria-label="Main sections">
              <Link
                to="/dashboard/seating"
                onClick={() => setActiveTab('seating')}
                className={`relative px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg transition-all duration-200 text-sm font-medium focus:outline-none flex-shrink-0 min-h-[44px] flex items-center justify-center
                  ${activeTab === 'seating'
                    ? 'text-white bg-wedding-sage shadow-md'
                    : 'text-wedding-dark-brown hover:text-wedding-sage hover:bg-wedding-sage/5'}`}
                role="tab"
                aria-selected={activeTab === 'seating'}
                aria-controls="seating-panel"
                id="seating-tab"
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <span>Seating</span>
                </div>
              </Link>
              <Link
                to="/dashboard/view"
                onClick={() => setActiveTab('view')}
                className={`relative px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg transition-all duration-200 text-sm font-medium focus:outline-none flex-shrink-0 min-h-[44px] flex items-center justify-center
                  ${activeTab === 'view'
                    ? 'text-white bg-wedding-sage shadow-md'
                    : 'text-wedding-dark-brown hover:text-wedding-sage hover:bg-wedding-sage/5'}`}
                role="tab"
                aria-selected={activeTab === 'view'}
                aria-controls="gallery-panel"
                id="gallery-tab"
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Gallery</span>
                </div>
              </Link>

              <Link
                to="/dashboard/upload"
                onClick={() => setActiveTab('upload')}
                className={`relative px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg transition-all duration-200 text-sm font-medium focus:outline-none flex-shrink-0 min-h-[44px] flex items-center justify-center
                  ${activeTab === 'upload'
                    ? 'text-white bg-wedding-sage shadow-md'
                    : 'text-wedding-dark-brown hover:text-wedding-sage hover:bg-wedding-sage/5'}`}
                role="tab"
                aria-selected={activeTab === 'upload'}
                aria-controls="upload-panel"
                id="upload-tab"
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>Upload</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </nav>
      <main className="flex-grow" role="main">
        <Routes>
          <Route
            path="/view"
            element={
              <div id="gallery-panel" role="tabpanel" aria-labelledby="gallery-tab">
                <PhotoGallery
                  selectionMode={selectionMode}
                  setSelectionMode={setSelectionMode}
                  refreshTrigger={refreshTrigger}
                />
              </div>
            }
          />
          <Route
            path="/seating"
            element={
              <div id="seating-panel" role="tabpanel" aria-labelledby="seating-tab">
                <Floorplan />
              </div>
            }
          />
          <Route
            path="/upload"
            element={
              <div id="upload-panel" role="tabpanel" aria-labelledby="upload-tab" className="px-4 py-6">
                <UploadForm
                  onUploadTabActivate={() => setActiveTab('upload')}
                  onUploadComplete={handleUploadComplete}
                />
              </div>
            }
          />
          <Route
            path="*"
            element={
              <div id="seating-panel" role="tabpanel" aria-labelledby="seating-tab">
                <Floorplan />
              </div>
            }
          />
        </Routes>
      </main>
    </div>
  );
};

export default Dashboard;